import { useEmailStore } from '@/stores/emailStore';
import { useFolderStore } from '@/stores/folderStore';
import { useAccountStore } from '@/stores/accountStore';
import { findIdentityById } from '@/lib/emailMerging';
import { isUnifiedAccountId, isUnifiedFolderId } from '@/constants/mailbox';
import type { Email } from '@/types/email';

export interface ResolvedEmailContext {
  email: Email | null;
  accountId: string;
  folderId: string;
}

/**
 * The single source of truth for "which real (account, folder) does this message
 * belong to?" — used by every per-account action (archive / delete / move /
 * star / read), whether triggered by a button or a keyboard shortcut.
 *
 * Two invariants that the older, duplicated resolvers kept getting wrong:
 *
 *   1. NEVER return a unified folder id. A per-account action must target a real
 *      mailbox; the active folder selection is a unified id ('all:inbox') in the
 *      All-accounts view, so falling back to it leaked that id into per-account
 *      APIs (which then silently no-op'd). We only fall back to the active
 *      selection when it is itself a real folder.
 *
 *   2. Look the message up across EVERY loaded source — the projected list, the
 *      open detail, the open thread, and the full email index — not just the
 *      visible `emails` array. Sender-group and thread members that aren't in the
 *      visible list still resolve, instead of returning null and dropping the
 *      action with no feedback.
 *
 * Returns null when no real (account, folder) can be determined; callers treat
 * that as "nothing actionable".
 */
export function resolveEmailContext(
  emailId: string,
  overrideFolderId?: string | null
): ResolvedEmailContext | null {
  if (!emailId) {
    return null;
  }
  const emailState = useEmailStore.getState();

  // Full-index lookup first: covers emails that are loaded but not currently
  // projected into the visible list (collapsed thread members, sender-group
  // members fetched for a digest, etc.).
  const identity = findIdentityById(emailState.emailReverseIndex, emailId);
  const indexEmail = identity ? emailState.emailIndex[identity] ?? null : null;
  const threadMessage =
    emailState.selectedThread?.messages.find((message) => message.id === emailId) ?? null;
  const email =
    indexEmail ??
    emailState.emails.find((item) => item.id === emailId) ??
    (emailState.selectedEmail?.id === emailId ? emailState.selectedEmail : null) ??
    threadMessage;

  // Account fallback: when the message object isn't loaded but a single (real)
  // account is selected, that account is unambiguous. Never use the unified
  // sentinel as an account — a per-account API call needs a real account.
  const selectedAccountId = useAccountStore.getState().selectedAccountId;
  const accountId =
    email?.accountId ??
    threadMessage?.accountId ??
    emailState.selectedThread?.accountId ??
    (selectedAccountId && !isUnifiedAccountId(selectedAccountId) ? selectedAccountId : null);

  // Resolve a REAL folder id, dropping any unified id at every step. Order:
  // explicit override → the message's own folder → the active selection (only if
  // it's a real folder, i.e. not the unified view).
  const activeFolderId = useFolderStore.getState().selectedFolderId();
  const realCandidates = [overrideFolderId, email?.folderId, activeFolderId];
  const folderId =
    realCandidates.find((value): value is string => Boolean(value) && !isUnifiedFolderId(value)) ??
    null;

  if (!accountId || !folderId) {
    return null;
  }
  return { email: email ?? null, accountId, folderId };
}
